import { Button, Dialog, IMessageProps, ImageUpload, ImageValue, Spinner } from 'Components/Core';
import { IDialogProps } from 'Components/Core/Dialog/types';
import { ListFieldSection } from 'Components/Layout';
import { actions as userAction } from 'Data/ReduxViewModels/User';
import { actions } from 'Data/ReduxViewModels/WorksheetEditor';
import { hasEmptyValue } from 'Lib/Any';
import { ExtractBase64FromDataUrl } from 'Lib/File';
import { Guid } from 'Lib/Guid';
import { useHasChangedAfterFirstLoad } from 'Lib/Hooks';
import { GetResizeSettingsFromOptionsModel } from 'Lib/Image';
import deepEquals from 'Lib/Object/deepEquals';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type UploadImageDialogProps = {
  worksheetId: Guid;
  imageId: Guid;
} & Pick<IDialogProps, 'visible' | 'onClose'>;

const UploadImageDialog: React.FC<UploadImageDialogProps> = ({ visible, onClose, worksheetId, imageId }) => {
  const [selectedImage, setSelectedImage] = useState<ImageValue | null>();

  const dispatch = useDispatch();
  const imageUploadId = `worksheetImageUpload_${imageId}`;
  const uploadingImage = useSelector((s) => s.worksheetEditor.status.uploadingImage);
  const uploadingStatus = useSelector((s) => s.worksheetEditor.status.uploadImageStatus);
  const processingImage = useSelector((s) => s.images.processing[imageUploadId]?.status === 'processing');

  // Ensure we have loaded the user's resize settings
  const fetchingUserFormSettings = useSelector((s) => s.user.isRetrievingFormSettings);
  const resizeOptionsNotAvailble = useSelector((s) => hasEmptyValue(s.user.formSettings?.ImageResizingSettings));
  const userId = useSelector((state) => state.user.data?.UUID);
  useEffect(() => {
    if (visible && resizeOptionsNotAvailble && !fetchingUserFormSettings) {
      dispatch(userAction.getUserForm({ userId }));
    }
  }, [visible, resizeOptionsNotAvailble, fetchingUserFormSettings, userId, dispatch]);

  const imageResizeSettings = useSelector(
    (s) => GetResizeSettingsFromOptionsModel(s.user.formSettings?.ImageResizingSettings),
    deepEquals
  );

  const onDialogClose = useCallback(() => {
    onClose?.();
    setSelectedImage(null);
  }, [onClose, setSelectedImage]);

  // Auto-close dialog when uploading succeeds
  // (We only have one uploading flag, so we assume that applies to this dialog)
  const uploadingStatusChanged = useHasChangedAfterFirstLoad(uploadingStatus);
  useEffect(() => {
    if (uploadingStatus === 'resolved' && uploadingStatusChanged) {
      onDialogClose();
    }
  }, [uploadingStatusChanged, uploadingStatus, dispatch, onDialogClose]);

  const uploadFile = () => {
    dispatch(
      actions.uploadImageAttachment({
        worksheetId,
        imageId,
        binaryData: ExtractBase64FromDataUrl(selectedImage!.dataUrl),
        fileName: selectedImage!.filename!,
      })
    );
  };

  const dialogButtons = [
    <Button onClick={onDialogClose} disabled={uploadingImage}>
      Cancel
    </Button>,
    <Button
      primary
      onClick={uploadFile}
      disabled={isEmpty(selectedImage) || uploadingImage || processingImage}
      loading={uploadingImage}
    >
      Upload and Save
    </Button>,
  ];

  let messages: IMessageProps[] = [];

  if (hasEmptyValue(imageResizeSettings)) {
    messages.push({
      severity: 'warning',
      message: 'Resizing options are not configured. Please contact your system administrator.',
    });
  }

  return (
    <Dialog
      onClose={uploadingImage ? undefined : onDialogClose}
      visible={visible}
      buttons={dialogButtons}
      title="Upload Image"
      width="700px"
    >
      {resizeOptionsNotAvailble ? (
        <div style={{ position: 'relative', height: `40px` }}>
          <Spinner />
        </div>
      ) : (
        <ListFieldSection wide messages={messages}>
          <ImageUpload
            id={imageUploadId}
            onChange={(e) => setSelectedImage(e?.data?.value)}
            resizeSettings={imageResizeSettings}
          />
        </ListFieldSection>
      )}
    </Dialog>
  );
};

export default UploadImageDialog;
